import { AudioLines, MessageSquare, ChevronLeft, ChevronRight, FileText } from 'lucide-react';

// CSS keyframes for natural waveform animation
const waveformStyles = `
  @keyframes waveform1 {
    0% { height: 4px; }
    100% { height: 16px; }
  }
  @keyframes waveform2 {
    0% { height: 4px; }
    100% { height: 12px; }
  }
  @keyframes waveform3 {
    0% { height: 4px; }
    100% { height: 18px; }
  }
  @keyframes waveform4 {
    0% { height: 4px; }
    100% { height: 10px; }
  }
  @keyframes waveform5 {
    0% { height: 4px; }
    100% { height: 14px; }
  }
`;
interface AgentDockProps {
  agentStatus: 'disconnected' | 'connecting' | 'authenticating' | 'requesting-microphone' | 'connected' | 'ending' | 'error';
  isUserMuted: boolean;
  isPushToTalkLocked: boolean;
  sessionDuration: string;
  isAgentActive: boolean;
  isAgentTyping: boolean;
  isUserSpeaking?: boolean;
  onConnectAgent: (mode?: string) => void;
  onPushToTalkStart: () => void;
  onPushToTalkEnd: () => void;
  onToggleLock: () => void;
  onStopConversation: () => void;
  floating?: boolean;
  column1Width?: number;
  hasCanvasContent?: boolean;
  // Optional chat input wiring (used only when disconnected and not floating)
  onSendMessage?: (message: string) => void;
  chatInputText?: string;
  onChatInputTextChange?: (text: string) => void;
  showChatInput?: boolean;
  hasMessages?: boolean;
  onShowChatInput?: () => void;
  onHideChatInput?: () => void;
  // Dual column toggles (used when canvas is present)
  isChatColumnVisible?: boolean;
  isNotebookColumnVisible?: boolean;
  onToggleChatColumn?: () => void;
  onToggleNotebookColumn?: () => void;
  showColumnToggles?: boolean;
}
const AgentDock = ({
  agentStatus,
  isUserMuted,
  isPushToTalkLocked,
  sessionDuration,
  isAgentActive,
  isAgentTyping,
  isUserSpeaking = false,
  onConnectAgent,
  onPushToTalkStart,
  onPushToTalkEnd,
  onToggleLock,
  onStopConversation,
  floating = false,
  column1Width = 100,
  hasCanvasContent = false,
  onSendMessage,
  chatInputText,
  onChatInputTextChange,
  showChatInput = false,
  hasMessages = false,
  onShowChatInput,
  onHideChatInput,
  isChatColumnVisible = false,
  isNotebookColumnVisible = false,
  onToggleChatColumn,
  onToggleNotebookColumn,
  showColumnToggles = false
}: AgentDockProps) => {
  // Calculate positioning for column 1 centering
  const getFloatingStyles = () => {
    if (!floating) return {};

    // Calculate the center of column 1
    const column1CenterPercent = hasCanvasContent ? column1Width / 2 : 50;
    return {
      left: `${column1CenterPercent}%`,
      transform: 'translateX(-50%)'
    };
  };
  const getStatusText = () => {
    switch (agentStatus) {
      case 'connected':
        return 'Connected';
      case 'connecting':
        return 'Connecting';
      case 'authenticating':
        return 'Authenticating';
      case 'requesting-microphone':
        return 'Requesting Mic';
      case 'ending':
        return 'Ending';
      case 'error':
        return 'Error';
      default:
        return 'Disconnected';
    }
  };

  // Floating pill uses a fixed comfortable width to avoid overflow issues
  // per spec (~495px with px-6/py-4 padding). No timer is shown.

  return (
    <>
      {/* Inject waveform animation styles */}
      <style dangerouslySetInnerHTML={{ __html: waveformStyles }} />
      
      <div className={`relative group ${floating ? "fixed bottom-6 z-[60]" : ""}`} style={floating ? getFloatingStyles() : {}}>

        {/* Collapsible Chat Input (only when disconnected and not floating and showChatInput is true) */}
        {!floating && agentStatus === 'disconnected' && showChatInput && (
          <div className="mb-2 flex items-start transition-all duration-300 ease-in-out max-h-96 opacity-100">
            {/* Close Button */}
            <button onClick={onHideChatInput} className="p-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0 mr-1">
              <svg className="w-4 h-4 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Chat Input Area */}
            <div className="flex flex-col gap-0.5 bg-white border border-gray-200 rounded-2xl p-1.5 flex-1">
              <textarea 
                value={chatInputText ?? ''} 
                onChange={e => onChatInputTextChange?.(e.target.value)} 
                placeholder="Type something..." 
                className="w-full p-2 bg-transparent border-0 focus:outline-none text-gray-900 placeholder-gray-500 text-[13px] resize-none overflow-y-auto" 
                rows={3} 
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    const msg = (chatInputText ?? '').trim();
                    if (msg && onSendMessage) {
                      onSendMessage(msg);
                      onChatInputTextChange?.('');
                    }
                  }
                }} 
              />
              <button 
                onClick={() => {
                  const msg = (chatInputText ?? '').trim();
                  if (msg && onSendMessage) {
                    onSendMessage(msg);
                    onChatInputTextChange?.('');
                  }
                }} 
                disabled={!(chatInputText ?? '').trim()} 
                className={`self-end p-2 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${(chatInputText ?? '').trim() ? 'bg-black text-white border border-black' : 'bg-white text-black border border-black'}`}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="19" x2="12" y2="5"></line>
                  <polyline points="5 12 12 5 19 12"></polyline>
                </svg>
              </button>
            </div>
          </div>
        )}
        <div className="mx-auto max-w-[600px] px-6 py-1">
          
          {/* ===== DISCONNECTED STATE ===== */}
          {agentStatus === 'disconnected' && !isAgentActive && (
            <div className="flex items-center justify-center gap-3">
              {/* Left: Toggle Chat Column */}
              {showColumnToggles && (
                <button 
                  onClick={onToggleChatColumn}
                  className={`w-10 h-10 flex items-center justify-center rounded-full transition-colors flex-shrink-0 ${
                    isChatColumnVisible 
                      ? 'bg-blue-100 hover:bg-blue-200 text-blue-700' 
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                  title={isChatColumnVisible ? 'Hide chat' : 'Show chat'}
                >
                  {isChatColumnVisible ? (
                    <ChevronLeft className="w-5 h-5" />
                  ) : (
                    <MessageSquare className="w-5 h-5" />
                  )}
                </button>
              )}

              {/* Center: Large "Talk to Studibudi" Button */}
              <button 
                className="relative flex-1 max-w-[400px] h-12 cursor-pointer group px-6 rounded-full bg-gradient-to-r from-green-200 to-emerald-200 hover:from-green-300 hover:to-emerald-300 transition-all duration-500 animate-pulse border border-gray-200 hover:border-gray-300 flex items-center justify-center"
                onClick={() => onConnectAgent('general')}
                style={{
                  animationDuration: '4s',
                  animationTimingFunction: 'ease-in-out'
                }}
              >
                <span className="text-slate-700 font-medium text-base group-hover:text-slate-800 transition-colors">
                  Talk to Studibudi
                </span>
              </button>

              {/* Center-Right: Start Button (round) */}
              <button 
                onClick={() => onConnectAgent('general')}
                className="w-10 h-10 flex items-center justify-center rounded-full transition-colors bg-green-100 hover:bg-green-200 text-green-700 flex-shrink-0"
                title="Start"
              >
                <AudioLines className="w-5 h-5" />
              </button>

              {/* Right: Toggle Notebook Column */}
              {showColumnToggles && (
                <button 
                  onClick={onToggleNotebookColumn}
                  className={`w-10 h-10 flex items-center justify-center rounded-full transition-colors flex-shrink-0 ${
                    isNotebookColumnVisible 
                      ? 'bg-purple-100 hover:bg-purple-200 text-purple-700' 
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                  title={isNotebookColumnVisible ? 'Hide notebook' : 'Show notebook'}
                >
                  {isNotebookColumnVisible ? (
                    <ChevronRight className="w-5 h-5" />
                  ) : (
                    <FileText className="w-5 h-5" />
                  )}
                </button>
              )}
            </div>
          )}

          {/* ===== CONNECTING/AUTHENTICATING STATE ===== */}
          {(agentStatus === 'connecting' || agentStatus === 'authenticating' || agentStatus === 'requesting-microphone' || agentStatus === 'ending') && (
            <div className="flex items-center justify-center gap-3">
              {/* Left: Back/Cancel Button (round) */}
              <button 
                onClick={onStopConversation}
                className="w-10 h-10 flex items-center justify-center rounded-full transition-colors bg-gray-100 hover:bg-gray-200 text-gray-700 flex-shrink-0"
                title="Cancel"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>

              {/* Center: Large "Connecting..." Button */}
              <div className="flex-1 max-w-[400px] h-12 px-6 rounded-full bg-gradient-to-r from-gray-200 to-slate-200 border border-gray-300/30 flex items-center justify-center gap-2">
                <div className="w-5 h-5 border-2 border-slate-600 border-t-transparent rounded-full animate-spin"></div>
                <span className="text-slate-700 font-medium text-base">
                  {getStatusText()}...
                </span>
              </div>

              {/* Right: Placeholder for symmetry */}
              <div className="w-10 h-10 flex-shrink-0"></div>
            </div>
          )}

          {/* ===== CONNECTED STATE ===== */}
          {agentStatus === 'connected' && (
            <div className="flex items-center justify-center gap-3">
              {/* Left: Toggle Chat Column */}
              {showColumnToggles && (
                <button 
                  onClick={onToggleChatColumn}
                  className={`w-10 h-10 flex items-center justify-center rounded-full transition-colors flex-shrink-0 ${
                    isChatColumnVisible 
                      ? 'bg-blue-100 hover:bg-blue-200 text-blue-700' 
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                  title={isChatColumnVisible ? 'Hide chat' : 'Show chat'}
                >
                  {isChatColumnVisible ? (
                    <ChevronLeft className="w-5 h-5" />
                  ) : (
                    <MessageSquare className="w-5 h-5" />
                  )}
                </button>
              )}

              {/* Center: Large "Push to Talk" Button with waveform and lock */}
              <div 
                className="relative flex-1 max-w-[400px] h-12 cursor-pointer group"
                onMouseDown={onPushToTalkStart}
                onMouseUp={onPushToTalkEnd}
                onTouchStart={onPushToTalkStart}
                onTouchEnd={onPushToTalkEnd}
              >
                {/* Animated background layer */}
                <div 
                  className={`absolute inset-0 transition-all duration-500 rounded-full border ${
                    !isUserMuted 
                      ? 'bg-gradient-to-r from-green-300 to-emerald-300 border-green-400/40 animate-pulse shadow-md shadow-green-200/40' 
                      : 'bg-gradient-to-r from-green-200 to-emerald-200 hover:from-green-300 hover:to-emerald-300 border-green-300/30 hover:border-green-400/40'
                  }`}
                  style={!isUserMuted ? {
                    animationDuration: '1.8s',
                    animationTimingFunction: 'ease-in-out',
                    transform: 'scale(1.01)'
                  } : {}}
                />
                
                {/* Content layer */}
                <div className="relative h-full flex items-center px-4">
                  {/* Voice Activity Waveform - left side */}
                  <div className="absolute left-6 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
                    {isUserSpeaking ? (
                      // Animated bars when speaking
                      <>
                        <div className="w-1.5 bg-green-600 rounded-full" style={{
                          height: '4px',
                          animation: 'waveform1 0.4s ease-in-out infinite alternate'
                        }} />
                        <div className="w-1.5 bg-green-600 rounded-full" style={{
                          height: '4px',
                          animation: 'waveform2 0.6s ease-in-out infinite alternate',
                          animationDelay: '0.1s'
                        }} />
                        <div className="w-1.5 bg-green-600 rounded-full" style={{
                          height: '4px',
                          animation: 'waveform3 0.5s ease-in-out infinite alternate',
                          animationDelay: '0.3s'
                        }} />
                        <div className="w-1.5 bg-green-600 rounded-full" style={{
                          height: '4px',
                          animation: 'waveform4 0.7s ease-in-out infinite alternate',
                          animationDelay: '0.2s'
                        }} />
                        <div className="w-1.5 bg-green-600 rounded-full" style={{
                          height: '4px',
                          animation: 'waveform5 0.45s ease-in-out infinite alternate',
                          animationDelay: '0.4s'
                        }} />
                      </>
                    ) : (
                      // Static dots when not speaking
                      <>
                        <div className="w-1.5 h-1.5 bg-green-600 rounded-full opacity-60" />
                        <div className="w-1.5 h-1.5 bg-green-600 rounded-full opacity-60" />
                        <div className="w-1.5 h-1.5 bg-green-600 rounded-full opacity-60" />
                        <div className="w-1.5 h-1.5 bg-green-600 rounded-full opacity-60" />
                        <div className="w-1.5 h-1.5 bg-green-600 rounded-full opacity-60" />
                      </>
                    )}
                  </div>

                  {/* Centered status text */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="flex items-center gap-2">
                      <span 
                        className={`text-base transition-transform duration-300 ${!isUserMuted ? 'animate-bounce' : ''}`}
                        style={!isUserMuted ? {
                          animationDuration: '1.5s',
                          animationIterationCount: 'infinite'
                        } : {}}
                      >
                        👋
                      </span>
                      <span className="text-slate-700 font-light text-sm group-hover:text-slate-800 transition-colors duration-500">
                        {!isUserMuted ? 'Speaking...' : 'Push to talk'}
                      </span>
                    </div>
                  </div>
                  
                  {/* Lock toggle button - right side */}
                  <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleLock();
                      }}
                      className={`flex items-center justify-center p-1.5 rounded-full shadow-sm transition-all duration-500 backdrop-blur-sm ${
                        isPushToTalkLocked 
                          ? 'bg-white/90 text-green-700 hover:bg-white group-hover:shadow-md' 
                          : 'bg-white/70 hover:bg-white/90 text-slate-600 hover:text-slate-700 group-hover:shadow-md'
                      }`}
                    >
                      {isPushToTalkLocked ? (
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10 2a5 5 0 00-5 5v2a2 2 0 00-2 2v5a2 2 0 002 2h10a2 2 0 002-2v-5a2 2 0 00-2-2H7V7a3 3 0 015.905-.75 1 1 0 001.937-.5A5.002 5.002 0 0010 2z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Center-Right: End Call Button (round) */}
              <button 
                onClick={onStopConversation}
                className="w-10 h-10 flex items-center justify-center rounded-full transition-colors bg-red-100 text-red-700 hover:bg-red-200 flex-shrink-0"
                title="End call"
              >
                <img src="/lovable-uploads/9f3c5e8d-3d49-4222-8cdd-653519226e4f.png" alt="End call" className="w-5 h-5" />
              </button>

              {/* Right: Toggle Notebook Column */}
              {showColumnToggles && (
                <button 
                  onClick={onToggleNotebookColumn}
                  className={`w-10 h-10 flex items-center justify-center rounded-full transition-colors flex-shrink-0 ${
                    isNotebookColumnVisible 
                      ? 'bg-purple-100 hover:bg-purple-200 text-purple-700' 
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                  title={isNotebookColumnVisible ? 'Hide notebook' : 'Show notebook'}
                >
                  {isNotebookColumnVisible ? (
                    <ChevronRight className="w-5 h-5" />
                  ) : (
                    <FileText className="w-5 h-5" />
                  )}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Floating dock effects */}
        {floating && (
          <>
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-pink-500/10 rounded-2xl blur-xl -z-10 opacity-50 group-hover:opacity-70 transition-opacity duration-300"></div>
            <div className="absolute top-full left-0 right-0 h-6 bg-gradient-to-b from-white/20 to-transparent rounded-b-2xl blur-sm opacity-30"></div>
          </>
        )}
      </div>
    </>
  );
};
export default AgentDock;